import { Alert, Form, Input, Modal, message, Progress, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  type AppArtifactUploadMetadata,
  appArtifactUploadApi,
  calculateAppArtifactFileSha256,
} from '@/services/appArtifactUploadApi';
import { applicationStore } from '@/stores';
import type { AppCatalogAppItem, CreateAdminAppVersionRequest } from '@/types';
import { formatBytes } from '../constants';

const { TextArea } = Input;
const { Dragger } = Upload;

interface VersionFormDialogProps {
  open: boolean;
  app: AppCatalogAppItem | null;
  onOpenChange: (open: boolean) => void;
}

interface VersionFormValues {
  versionId: string;
  gitSha?: string;
  artifactSignatureRef?: string;
  manifestDigest?: string;
  runtimeBootstrap: string;
  runtimeHealthcheckPath: string;
  releaseNotes?: string;
  reason?: string;
}

interface PendingArtifactMetadata {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  lastModified: number;
  sha256?: string;
}

function trimOptional(value?: string) {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function zipContentType(file: File) {
  return file.type.trim() || 'application/zip';
}

function versionIdFromZipFilename(fileName: string) {
  const value = fileName.replace(/\.zip$/i, '').trim();
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(value) ? value : '';
}

export const VersionFormDialog = observer(function VersionFormDialog({
  open,
  app,
  onOpenChange,
}: VersionFormDialogProps) {
  const [form] = Form.useForm<VersionFormValues>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingArtifact, setPendingArtifact] =
    useState<PendingArtifactMetadata | null>(null);
  const [uploadedArtifact, setUploadedArtifact] =
    useState<AppArtifactUploadMetadata | null>(null);
  const [uploadedForVersionId, setUploadedForVersionId] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [calculatingSha, setCalculatingSha] = useState(false);

  const uploadFileList = useMemo<UploadFile[]>(() => {
    if (!selectedFile) {
      return [];
    }

    const fileItem: UploadFile = {
      uid: selectedFile.name,
      name: selectedFile.name,
      size: selectedFile.size,
    };

    if (uploadedArtifact) {
      fileItem.status = 'done';
    }

    return [fileItem];
  }, [selectedFile, uploadedArtifact]);

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      runtimeBootstrap: 'bootstrap.sh',
      runtimeHealthcheckPath: '/',
    });
  }, [form, open]);

  const resetUploadState = () => {
    setSelectedFile(null);
    setPendingArtifact(null);
    setUploadedArtifact(null);
    setUploadedForVersionId('');
    setUploadProgress(0);
    setUploading(false);
    setCalculatingSha(false);
  };

  const handleClose = () => {
    onOpenChange(false);
    form.resetFields();
    resetUploadState();
  };

  const handleZipFileSelect = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      message.error('请选择 .zip 文件');
      return Upload.LIST_IGNORE;
    }

    const nextArtifact: PendingArtifactMetadata = {
      fileName: file.name,
      contentType: zipContentType(file),
      sizeBytes: file.size,
      lastModified: file.lastModified,
    };
    const currentVersionId = form.getFieldValue('versionId')?.trim();
    const inferredVersionId = versionIdFromZipFilename(file.name);
    if (!currentVersionId && inferredVersionId) {
      form.setFieldsValue({ versionId: inferredVersionId });
    }

    setSelectedFile(file);
    setPendingArtifact(nextArtifact);
    setUploadedArtifact(null);
    setUploadedForVersionId('');
    setUploadProgress(0);
    setCalculatingSha(true);
    void calculateAppArtifactFileSha256(file)
      .then((sha256) => {
        setPendingArtifact((current) =>
          current?.fileName === file.name &&
          current.sizeBytes === file.size &&
          current.lastModified === file.lastModified
            ? { ...current, sha256 }
            : current,
        );
      })
      .catch((error) => {
        console.error('计算 artifact SHA256 失败:', error);
        message.error('计算 SHA256 失败');
      })
      .finally(() => setCalculatingSha(false));

    return false;
  };

  const uploadArtifactIfNeeded = async (
    appId: string,
    versionId: string,
  ): Promise<AppArtifactUploadMetadata> => {
    if (!selectedFile) {
      throw new Error('请选择应用 zip 包');
    }

    if (uploadedArtifact && uploadedForVersionId === versionId) {
      return uploadedArtifact;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const artifact = await appArtifactUploadApi.uploadZipArtifact({
        appId,
        versionId,
        file: selectedFile,
        sha256: pendingArtifact?.sha256,
        onProgress: (progress) => setUploadProgress(progress.percent),
      });
      setUploadedArtifact(artifact);
      setUploadedForVersionId(versionId);
      setUploadProgress(100);
      form.setFieldsValue({ artifactSignatureRef: artifact.objectKey });
      return artifact;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!app) {
      return;
    }

    const values = await form.validateFields();
    const versionId = values.versionId.trim();

    try {
      const artifact = await uploadArtifactIfNeeded(app.appId, versionId);
      const payload: CreateAdminAppVersionRequest = {
        appId: app.appId,
        versionId,
        gitSha: trimOptional(values.gitSha),
        artifactKind: artifact.artifactKind,
        artifactUrl: artifact.artifactUrl,
        artifactSha256: artifact.artifactSha256,
        artifactSizeBytes: artifact.artifactSizeBytes,
        artifactSignatureRef:
          trimOptional(values.artifactSignatureRef) || artifact.objectKey,
        manifestDigest: trimOptional(values.manifestDigest),
        runtimeBootstrap: values.runtimeBootstrap.trim() || 'bootstrap.sh',
        runtimeHealthcheckPath: values.runtimeHealthcheckPath.trim() || '/',
        releaseNotes: trimOptional(values.releaseNotes),
        reason: trimOptional(values.reason),
      };
      await applicationStore.createVersion(payload);
      message.success('版本已创建');
      handleClose();
    } catch (error) {
      console.error('创建版本失败:', error);
      message.error(error instanceof Error ? error.message : '创建版本失败');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={app ? `新增版本 - ${app.displayName}` : '新增版本'}
      centered
      width={760}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            loading={applicationStore.savingVersion || uploading}
            onClick={() => void handleSubmit()}
          >
            {uploading ? '上传中...' : '创建'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" requiredMark={false} className="pt-2">
        <div className="grid gap-4 md:grid-cols-2">
          <Form.Item
            name="versionId"
            label="Version ID"
            rules={[
              { required: true, message: '请输入 Version ID' },
              {
                pattern: /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/,
                message: '仅支持字母、数字、点、下划线和连字符',
              },
            ]}
          >
            <Input className="font-mono" placeholder="v0.1.0" />
          </Form.Item>

          <Form.Item name="gitSha" label="Git SHA">
            <Input className="font-mono" placeholder="可选" />
          </Form.Item>
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-foreground">
            应用 zip 包 *
          </div>
          <Dragger
            accept=".zip,application/zip,application/x-zip-compressed"
            beforeUpload={(file) => handleZipFileSelect(file)}
            disabled={uploading}
            fileList={uploadFileList}
            maxCount={1}
            onRemove={resetUploadState}
          >
            <div className="py-5">
              <p className="text-sm font-medium text-foreground">
                点击或拖拽上传 .zip
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                提交时会创建 App Center 上传会话，使用短期 STS 分片直传
                S3，并自动写入 URL、SHA256 和大小。
              </p>
            </div>
          </Dragger>

          {selectedFile ? (
            <div className="rounded-md border border-border bg-gray-50 px-3 py-2 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">
                {selectedFile.name} · {formatBytes(selectedFile.size)}
              </div>
              {pendingArtifact ? (
                <div className="mt-2 grid gap-1 font-mono sm:grid-cols-2">
                  <div>artifactKind: zip</div>
                  <div>contentType: {pendingArtifact.contentType}</div>
                  <div>artifactSizeBytes: {pendingArtifact.sizeBytes}</div>
                  <div className="break-all sm:col-span-2">
                    artifactSha256:{' '}
                    {pendingArtifact.sha256 ??
                      (calculatingSha ? '计算中...' : '-')}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {uploading ? (
            <Progress percent={uploadProgress} size="small" />
          ) : null}

          {uploadedArtifact ? (
            <Alert
              type="success"
              showIcon
              message="Artifact 已上传"
              description={
                <div className="space-y-1 font-mono text-xs">
                  <div className="break-all">
                    {uploadedArtifact.artifactUrl}
                  </div>
                  <div>{uploadedArtifact.artifactSha256}</div>
                  <div className="break-all">{uploadedArtifact.objectKey}</div>
                </div>
              }
            />
          ) : null}
        </div>

        <Form.Item name="artifactSignatureRef" label="签名引用">
          <Input placeholder="默认使用上传 objectKey，可选覆盖" />
        </Form.Item>

        <div className="grid gap-4 md:grid-cols-3">
          <Form.Item name="manifestDigest" label="Manifest Digest">
            <Input className="font-mono" placeholder="服务端可自动生成" />
          </Form.Item>

          <Form.Item
            name="runtimeBootstrap"
            label="Runtime Bootstrap"
            rules={[{ required: true, message: '请输入启动脚本' }]}
          >
            <Input placeholder="bootstrap.sh" />
          </Form.Item>

          <Form.Item
            name="runtimeHealthcheckPath"
            label="Healthcheck Path"
            rules={[{ required: true, message: '请输入健康检查路径' }]}
          >
            <Input placeholder="/" />
          </Form.Item>
        </div>

        <Form.Item name="releaseNotes" label="Release Notes">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="reason" label="操作原因">
          <TextArea rows={2} placeholder="写入审计日志，可选" />
        </Form.Item>
      </Form>
    </Modal>
  );
});
